import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { BoardContactsUpdateModel } from 'api/client'
import { IMessageRecipient } from 'Modules/Communication/shared/models/Inbox'
import {
  ILoadingMessageRecipient,
  IMessageReciept,
  IMessageRecieptRecipient,
  SendMessageStateEnum,
  SendMessageStatusEnum,
} from 'shared/models/Message'
import { initialSendSmsState } from '../shared/constants'
import { SendSmsDraft } from '../shared/SendMessage'

type OpenSmsPayload = { message?: SendSmsDraft; draftId?: string }

type RemovedSmsRecipient = Partial<
  Pick<IMessageRecipient, 'nameregisterId' | 'phonenumber' | 'recipientType'>
>

const sendSmsSlice = createSlice({
  name: 'sendSms',
  initialState: initialSendSmsState,
  reducers: {
    openSmsCompose: (state, action: PayloadAction<OpenSmsPayload>) => {
      state.state = SendMessageStateEnum.send
      state.message = action.payload.message || null
      state.messageId = action.payload.draftId || null
      state.isEditDraft = !!action.payload.draftId
    },
    closeSms: (state) => {
      state.state = null
    },
    setSmsCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isCollapsed = action.payload
    },
    smsSendStarted: (state) => {
      state.processing = true
    },
    smsSent: (state) => {
      state.processing = false
      state.state = SendMessageStateEnum.reciept
    },
    smsSendFailed: (state, action: PayloadAction<string>) => {
      state.processing = false
      state.error = action.payload
    },
    smsDraftCreated: (
      state,
      action: PayloadAction<{ id: string; message: SendSmsDraft }>
    ) => {
      state.message = action.payload.message
      state.messageId = action.payload.id
    },
    smsDraftUpdateStarted: (state) => {
      state.isDraftUpdated = true
    },
    smsDraftUpdated: (state, action: PayloadAction<string>) => {
      if (state.message) state.message.content = action.payload
    },
    smsReceiptRequested: (state, action: PayloadAction<string>) => {
      state.reciept.loading = true
      state.reciept.error = null
      state.reciept.messageId = action.payload
    },
    smsReceiptLoaded: (state, action: PayloadAction<IMessageReciept>) => {
      state.reciept.data = action.payload
      state.reciept.loading = false
    },
    smsReceiptFailed: (state, action: PayloadAction<string>) => {
      state.reciept.loading = false
      state.reciept.error = action.payload
    },
    smsSendingSucceeded: (state) => {
      if (state.reciept.data)
        state.reciept.data.sendingStatus = SendMessageStatusEnum.Sent
    },
    smsSendingFailed: (state, action: PayloadAction<string>) => {
      state.reciept.loading = false
      state.reciept.error = action.payload
      if (state.reciept.data)
        state.reciept.data.sendingStatus = SendMessageStatusEnum.Error
    },
    smsSendingStatusRefetched: (
      state,
      action: PayloadAction<IMessageReciept['sendingStatus']>
    ) => {
      if (state.reciept.data) state.reciept.data.sendingStatus = action.payload
    },
    setSmsCreateContact: (
      state,
      action: PayloadAction<IMessageRecieptRecipient | null>
    ) => {
      state.reciept.createContact = action.payload
      state.reciept.error = null
    },
    smsContactCreateStarted: (state) => {
      state.reciept.processing = true
    },
    smsContactCreated: (
      state,
      action: PayloadAction<BoardContactsUpdateModel>
    ) => {
      state.reciept.createContact = null
      state.reciept.processing = false

      const updated = state.reciept.data?.recipients?.find(
        (x) => x.phonenumber === action.payload.mobile
      )
      if (updated) updated.isAdded = true
    },
    smsContactCreateFailed: (state, action: PayloadAction<string>) => {
      state.reciept.processing = false
      state.reciept.error = action.payload
    },
    smsRecipientAdded: (
      state,
      action: PayloadAction<Partial<IMessageRecipient>>
    ) => {
      if (!state.message.recipients?.length) {
        state.message.recipients = [action.payload]
        return
      }

      const existing = state.message.recipients.find(
        (x) =>
          x.phonenumber?.toLowerCase() ===
          action.payload.phonenumber?.toLowerCase()
      )
      if (existing) return

      state.isDraftUpdated = true
      state.message.recipients.push(action.payload)
    },
    smsRecipientsAdded: (
      state,
      action: PayloadAction<Partial<IMessageRecipient>[]>
    ) => {
      if (!state.message.recipients?.length) {
        state.message.recipients = action.payload
        return
      }

      const current = state.message.recipients
      const fresh = action.payload.filter(
        (x) =>
          !current.some(
            (y) => y.phonenumber?.toLowerCase() === x.phonenumber?.toLowerCase()
          )
      )

      state.isDraftUpdated = true
      state.message.recipients = [...current, ...fresh]
    },
    smsRecipientRemoved: (
      state,
      action: PayloadAction<RemovedSmsRecipient>
    ) => {
      state.isDraftUpdated = true
      state.message.recipients = state.message.recipients?.filter(
        (x) =>
          x.nameregisterId !== action.payload.nameregisterId ||
          (x.phonenumber !== action.payload.phonenumber &&
            Boolean(x.phonenumber))
      )
    },
    smsRecipientsLoadingStarted: (
      state,
      action: PayloadAction<ILoadingMessageRecipient[]>
    ) => {
      state.recipientLoadingQueue = [
        ...(state.recipientLoadingQueue ?? []),
        ...action.payload,
      ]
    },
    smsRecipientsLoadingFinished: (
      state,
      action: PayloadAction<ILoadingMessageRecipient[]>
    ) => {
      state.recipientLoadingQueue = state.recipientLoadingQueue?.filter(
        (x) =>
          !action.payload.some(
            (y) =>
              y.id === x.id &&
              y.type === x.type &&
              y.recipientType === x.recipientType
          )
      )
    },
    resetSms: () => initialSendSmsState,
  },
})

export default sendSmsSlice
